import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';

// Layout Components
import AuthLayout from './components/Layouts/AuthLayout';
import DashboardLayout from './components/Layouts/DashboardLayout';
import MainLayout from './components/Layouts/MainLayout';
import Root from './components/Layouts/Root';

// Auth Pages
import AuthPage from './pages/auth/AuthPage';
import AuthComplete from './pages/auth/AuthComplete';
import PlanSelection from './pages/auth/PlanSelection';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import RegisterInvite from './pages/auth/RegisterInvite';

// Static Pages
import Home from './pages/static/Home';
import SubmitFlyer from './pages/static/SubmitFlyer';
import Services from './pages/static/Services';
import Quote from './pages/static/Quote';
import Contact from './pages/static/Contact';
import About from './pages/static/About';
import Location from './pages/static/Location';
import Terms from './pages/static/Terms';
import Privacy from './pages/static/Privacy';
import Testimonials from './pages/static/Testimonials';
import TrackShipment from './pages/static/TrackShipment';
// Career Pages
import BecomeRider from './pages/static/career/BecomeRider';
import DriverApplication from './pages/static/career/DriverApplication';
import PartnerApplication from './pages/static/career/PartnerApplication';

import RoleSelection from './pages/auth/RoleSelection';
import SubscriptionConfirm from './pages/ecommerce/SubscriptionConfirm';
import SavedShipments from './pages/dashboards/Agent/SavedShipments';

// Error Pages
import NotFound from './pages/errors/NotFound';
import ServerError from './pages/errors/ServerError';

// Admin Dashboard Pages
import AdminDashboard from './pages/dashboards/Admin/AdminDashboard';
import ManageUser from './pages/dashboards/Admin/ManageUsers';
import Reports from './pages/dashboards/Admin/Reports';
import PricingConfig from './pages/dashboards/Admin/PricingConfig';
import StatusDescriptions from './pages/dashboards/Admin/StatusDescriptions';
import EcommercePricing from './pages/dashboards/Admin/EcommercePricing';
import ManagePlans from './pages/dashboards/Admin/ManagePlans';
import ManageSubscriptions from './pages/dashboards/Admin/ManageSubscriptions';
import AdminUpdateTracking from './pages/dashboards/Admin/UpdateTracking';
import ManageBundles from './pages/dashboards/Admin/ManageBundles';
import RevenueDashboard from './pages/dashboards/Admin/RevenueDashboard';
import RequireAdmin from './routes/RequireAdmin';
import RequireAuth from './routes/RequireAuth';
import RequireRole from './routes/RequireRole';

// Agent Dashboard Pages
import AgentDashboard, { AgentDashboardHome as AgentHome } from './pages/dashboards/Agent/AgentDashboard';
import ManageShipments from './pages/dashboards/Shared/ManageShipments';
import UpdateTracking from './pages/dashboards/Agent/Shipments/UpdateTracking';
import AgentBookShipment from './pages/dashboards/Agent/AgentBookShipment';
import AgentProfile from './pages/dashboards/Agent/AgentProfile';

// User Dashboard Pages
import UserDashboard, { UserDashboardHome as UserHome } from './pages/dashboards/User/UserDashboard';
import UserProfile from './pages/dashboards/User/Profile';
import MyShipments from './pages/dashboards/User/Shipments/MyShipments';
import NewShipment from './pages/dashboards/User/Shipments/NewShipment';
import Tracking from './pages/dashboards/User/Shipments/Tracking';
import PaystackCallback from './pages/dashboards/User/Payments/PaystackCallback';
import Bundles from './pages/dashboards/User/Bundles';

// Ecommerce Dashboard Pages
import EcommerceDashboard from './pages/dashboards/Ecommerce/EcommerceDashboard';
import ShipmentHistory from './pages/dashboards/Ecommerce/ShipmentHistory';
import BookShipment from './pages/dashboards/Ecommerce/BookShipment';
import EcommerceProfile from './pages/dashboards/Ecommerce/Profile';

// Define routes using createBrowserRouter
const appRouter = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    children: [
      {
        path: '/',
        element: <MainLayout />,
        children: [
          { index: true, element: <Home /> },
          { path: 'submit-flyer', element: <SubmitFlyer /> },
          { path: 'services', element: <Services /> },
          { path: 'quote', element: <Quote /> },
          { path: 'contact', element: <Contact /> },
          { path: 'about', element: <About /> },
          { path: 'location', element: <Location /> },
          { path: 'terms', element: <Terms /> },
          { path: 'privacy', element: <Privacy /> },
          { path: 'testimonials', element: <Testimonials /> },
          { path: 'track', element: <TrackShipment /> },
          { path: 'track/:id', element: <TrackShipment /> },
          // Career
          { path: 'career/rider', element: <BecomeRider /> },
          { path: 'career/driver', element: <DriverApplication /> },
          { path: 'career/partner', element: <PartnerApplication /> },
        ],
      },
      {
        path: 'auth',
        element: <AuthLayout />,
        children: [
          { index: true, element: <AuthPage /> },
          { path: 'complete', element: <AuthComplete /> },
          { path: 'role-selection', element: <RoleSelection /> },
          { path: 'plan-selection', element: <PlanSelection /> },
          { path: 'forgot', element: <ForgotPassword /> },
          { path: 'reset', element: <ResetPassword /> },
          { path: 'request-password-reset', element: <ForgotPassword /> },
          { path: 'reset-password/:token', element: <ResetPassword /> },
          { path: 'register-invite', element: <RegisterInvite /> },
        ],
      },
      {
        path: 'dashboard',
        element: <DashboardLayout />,
        errorElement: <ServerError />,
        children: [
          { index: true, element: <Navigate to="/dashboard/user" replace /> },
          // Admin Routes
          {
            path: 'admin/*',
            element: <RequireAdmin />,
            children: [
              { index: true, element: <AdminDashboard /> },
              { path: 'users', element: <ManageUser /> },
              { path: 'reports', element: <Reports /> },
              { path: 'pricing', element: <PricingConfig /> },
              { path: 'status-descriptions', element: <StatusDescriptions /> },
              { path: 'ecommerce-pricing', element: <EcommercePricing /> },
              { path: 'plans', element: <ManagePlans /> },
              { path: 'subscriptions', element: <ManageSubscriptions /> },
              { path: 'bundles', element: <ManageBundles /> },
              { path: 'revenue', element: <RevenueDashboard /> },
              { path: 'tracking', element: <AdminUpdateTracking /> },
              { path: 'referral-leaderboard', element: <Navigate to="/dashboard/admin" replace /> },
            ],
          },
          // Agent Routes
          {
            path: 'agent/*',
            element: <RequireRole role="agent" />,
            children: [
              {
                element: <AgentDashboard />,
                children: [
                  { index: true, element: <AgentHome /> },
                  { path: 'shipments', element: <ManageShipments /> },
                  { path: 'tracking', element: <UpdateTracking /> },
                  { path: 'book-shipment', element: <AgentBookShipment /> },
                  { path: 'saved', element: <SavedShipments /> },
                  { path: 'profile', element: <AgentProfile /> },
                  { path: 'referrals', element: <Navigate to="/dashboard/agent" replace /> },
                ],
              },
            ],
          },
          // User Routes
          {
            path: 'user/*',
            element: <RequireAuth />,
            children: [
              {
                element: <UserDashboard />,
                children: [
                  { index: true, element: <UserHome /> },
                  { path: 'profile', element: <UserProfile /> },
                  { path: 'shipments', element: <MyShipments /> },
                  { path: 'shipments/new', element: <NewShipment /> },
                  { path: 'shipments/tracking', element: <Tracking /> },
                  { path: 'shipments/tracking/:id', element: <Tracking /> },
                  { path: 'payments', element: <Navigate to="/dashboard/user/shipments" replace /> },
                  { path: 'payments/callback', element: <PaystackCallback /> },
                  { path: 'bundles', element: <Bundles /> },
                  { path: 'referrals', element: <Navigate to="/dashboard/user" replace /> },
                ],
              },
            ],
          },
          // Ecommerce Routes
          {
            path: 'ecommerce/*',
            element: <RequireRole role="ecommerce" />,
            children: [
              { index: true, element: <EcommerceDashboard /> },
              { path: 'shipment-history', element: <ShipmentHistory /> },
              { path: 'shipments/tracking/:id', element: <Tracking /> },
              { path: 'book-shipment', element: <BookShipment /> },
              { path: 'profile', element: <EcommerceProfile /> },
              { path: 'bundles', element: <Bundles /> },
            ],
          },
        ],
      },
      {
        path: 'subscription/confirm',
        element: <SubscriptionConfirm />
      },
      {
        path: 'server-error',
        element: <ServerError />,
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
]);

// App Component
const App = () => {
  return <RouterProvider router={appRouter} />;
};

export default App;
